import {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query";

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getAccessTokenUsingRefreshToken } from "apis/token";
import dayjs from "dayjs";
import jwt_decode from "jwt-decode";
import { http, HttpResponse } from "msw";
import {
  openFreeSubscription,
  openUpgradePlanModal,
  setRequiredSubscriptionId,
} from "store/slices/subscriptionModal";

import { onTransactionUpdateFailure } from "store/slices/transactions";
import { isInkleMobileApp } from "util/isInkleMobileApp";

type QueryReturnValue<T = unknown, E = unknown, M = unknown> =
  | {
      error: E;
      data?: undefined;
      meta?: M;
    }
  | {
      error?: undefined;
      data: T;
      meta?: M;
    };

const getAccessToken = async () => {
  let authInLocal;
  try {
    authInLocal = localStorage.getItem("authTokens");
    authInLocal = JSON.parse(authInLocal || "{}");

    if (!authInLocal.access) {
      throw new Error("TOKEN_NOT_FOUND");
    }

    const user = jwt_decode<{ exp: number }>(authInLocal.access);
    const isExpired = dayjs.unix(user.exp).diff(dayjs()) < 1;

    if (isExpired) {
      throw new Error("TOKEN_EXPIRED");
    }

    if (authInLocal.access && !isExpired) {
      return authInLocal.access;
    }
  } catch (error: any) {
    const message = (error as Error).message;

    if (message === "TOKEN_EXPIRED") {
      try {
        const { access } = await getAccessTokenUsingRefreshToken({
          refresh: authInLocal.refresh,
        });
        localStorage.setItem(
          "authTokens",
          JSON.stringify({ ...authInLocal, access })
        );
      } catch (error) {
        return false;
      }
    }
  }
};

export const baseURL = process.env.PUBLIC_BASE_URL;

const query = fetchBaseQuery({
  baseUrl: baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

const signout = () => {
  localStorage.removeItem("authTokens");
  window.location.href = `${window.location.origin}/signin`;
};

// exchange_token/ (bank connection OAuth code exchange) can legitimately 401
// on an already-used or expired authorization code — a business-logic
// failure, not an invalid session. On mobile this redirect lands in the
// in-app browser/WebView handoff, where that 401 was wrongly signing out an
// otherwise-valid session; ConnectionConfirmation.tsx's own catch already
// surfaces the error, so skip the blanket signout here instead.
const isExchangeTokenRequest = (args: string | FetchArgs): boolean => {
  const url = typeof args === "string" ? args : args.url;
  return url.includes("/exchange_token/");
};

export const handleSubscribscriptionBlocker = (
  dispatch: BaseQueryApi["dispatch"],
  error: { error: { code?: string; message: string } }
) => {
  const authInLocal = localStorage.getItem("authTokens");
  const parsedAuth = JSON.parse(authInLocal || "");

  const requiredSubscriptionId = error.error.message;

  if (parsedAuth.is_public_user) {
    dispatch(openFreeSubscription());
  } else {
    dispatch(setRequiredSubscriptionId(requiredSubscriptionId));
    dispatch(openUpgradePlanModal());
  }
};

const handleTransactionUpdateFailure = (
  dispatch: BaseQueryApi["dispatch"],
  error: { error: { code?: string; message: string } }
) => {
  if (error.error.code) {
    dispatch(onTransactionUpdateFailure(error.error.code));
  }
};

export const handleDebterBlock = () => {
  const path = window.location.pathname;
  if (!path.includes("/billing") && !path.includes("/settings/services")) {
    window.location.href = `${window.location.origin}/billing/payment-pending`;
  }
};

const baseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let accessToken = null;
  try {
    accessToken = await getAccessToken();
  } catch (error) {}
  const authInLocal = JSON.parse(localStorage.getItem("authTokens") || "{}");

  const headers = new Headers();

  if (accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
    headers.set("request-path", window.location.href);
  }

  let queryParams: string | FetchArgs = { url: "" };

  if (typeof args === "object") {
    queryParams = { ...args, headers };
  } else {
    queryParams = { url: args, headers };
  }

  let result = await query(queryParams, api, extraOptions);

  if (result.error) {
    let error = result?.error.data as {
      error: { code?: string; message: string };
    };

    if (
      result?.error?.status === 401 &&
      !(isInkleMobileApp() && isExchangeTokenRequest(args))
    ) {
      signout();
    }

    if (result?.error?.status === 400) {
      handleTransactionUpdateFailure(api.dispatch, error);
    }

    if (result?.error?.status === 402) {
      if (error.error.code === "pending_dues") {
        if (authInLocal?.is_service_user) {
          return {
            error: result.error,
          };
        }
        handleDebterBlock();
        return {
          error: result.error,
        };
      }

      handleSubscribscriptionBlocker(api.dispatch, error);
      error.error.message =
        "You don't have access to this feature. Please upgrade your subscription to continue.";
      result.error.data = error;
    }

    return {
      error: result.error,
    };
  }

  return {
    ...result,
    data: (result.data as { data: unknown } | null)?.data,
  } as QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>;
};

export const emptyApi = createApi({
  tagTypes: [
    "FILINGS_DASHBOARD",
    "TAX_SETUP_ANSWERS",
    "TAX_DETECTED_FILINGS",
    "TAX_ENTITIES_CART_TOTAL",
    "DOMAIN_RECOGNITION",
    "API_TOKEN",
    "ServiceMembers",
    "Tasks",
    "Calender",
    "PlaidBanks",
    "CalendarPdf",
    "Referral",
    "CompanyWForm",
    "Transactions",
    "ChatTemplate",
    "CrmCompanyGroups",
    "ChatBroadcast",
    "ChannelListQuery",
    "Task7004",
    "Quotes",
    "BooksOnboarding",
    "ChatTickets",
    "ChatOverview",
    "SalesCrmCompanyGroups",
    "SalesCrmProfiles",
    "CalendarReminder",
    "TeamSettingGroups",
    "ChannelDraft",
    "TP_INVOICES",
    "TP_PARTNER_ADDRESS",
    "TP_TRANSFERS",
    "TP_ACCOUNTINFO",
    "AUTOFILL",
    "AP_AUTOFILLS",
    "PEOPLE",
    "PERSONA_ROLE_HISTORY",
    "TaskTemplate",
    "CustomerChatTickets",
    "GroupCoupon",
    "TaskList",
    "TeamMember",
    "RequestedTeamMember",
    "Groups",
    "STAKEHOLDERS",
    "SavedCards",
    "NOTIFICATION",
    "REGISTER_AGENT",
    "FCA",
    "PreIncorpList",
    "TicketNotes",
    "BooksCRM",
    "PerkList",
    "GetFilings",
    "CPATeamManagement",
    "Subscriptions",
    "OnboardingProducts",
    "BillingInvoices",
    "OnboardingSteps",
    "OPEN_ITEMS",
    "SliderCRM",
    "INVOICES",
    "INVOICE_SOURCE_SYNC",
    "INVOICE_CUSTOMERS",
    "INVOICE_SETTING",
    "INVOICE_ITEMS",
    "GLOBAL_COMPLIANCE",
    "CHART_OF_ACCOUNTS",
    "Merchants",
    "DOCUMENT_REQUEST",
    "BOOKS_TRANSACTIONS",
    "BOOKS_SINGLE_TRANSACTION",
    "CART",
    "RECONCILIATION",
    "VENDORS",
    "GENERAL_LEDGER",
    "CONNECTION",
    "BILLING_INFO",
    "REPORTS",
    "TRANSACTION_MERCHANTS",
    "CREDITS",
    "BOOKS_CRM",
    "PROFILE",
    "CAPTBALE",
    "NEXUSTRACKER",
    "INTERACTIVE_REPORTS",
    "RULE_ENGINE",
    "INTERNAL_TEAM_SETTINGS",
    "JOURNAL_ENTRIES",
    "FIXED_ASSET",
    "COA_START_DATE",
    "MESSAGE_DETAILS",
    "ALL_SHARED_MESSAGE",
    "MAILROOM",
    "MAILROOM_DEFAULT_ADDRESS",
    "RECENT_DOCUMENT",
    "CALL_HISTORY",
    "CHAT_SETTINGS",
    "DOCUMENT_LIST",
    "DOCUMENT_TAG_REVIEW",
    "FSPGroups",
    "PracticeTeamManagement",
    "All_SERVICE_PROVIDERS",
    "ENTITY_DATA_ATTRIBUTES",
    "ALARMS",
    "FILINGS_DASHBOARD",
    "ALL_COMPLIANCES",
    "MISSING_DATA_POINTS",
    "PRACTICE_BILLING_INFO",
    "PRACTICE_PAYMENT_METHODS",
    "PRACTICE_AUTOFILL",
    "PRACTICE_INVOICES",
    "MONEY_IN_TRANSIT",
    "AGENT_AVAILABILITY",
    "BOOKS_QUOTES",
    "SHAREHOLDERS",
    "LIVE_REPORT",
    "VARIANCE_EXPLANATION",
    "FORECAST_REPORT",
    "FORECAST_SETTINGS",
    "AUDIT_LOG_BY_OBJECT",
    "CALL_DETAILS",
    "BREX_POINTS",
    "PRODUCTS_AND_SERVICES",
    "TAX_RATES",
    "SENSITIVE_DATA",
    "SENSITIVE_DATA_ACCESS",
    "TASK_ALARM",
    "FEDERAL_REGISTRATIONS",
    "STATE_REGISTRATIONS",
    "STATE_REGISTRATION_SUGGESTIONS",
    "PLATFORM_SUBSCRIPTION_PRODUCT",
    "LINKED_INVOICES",
    "SingleBillingInvoice",
    "TEAMS_FOR_CHANNEL",
    "MEMBERS_FOR_CHANNEL",
    "NOTES",
    "PIN_NOTES",
    "FORM_1099_TEMPLATE",
    "TICKET_ETD_HISTORY",
    "AGING_SUMMARY",
    "REMINDERS",
    "ALL_ACTION_ITEMS",
    "CUSTOMER_ADDRESSES",
    "INVOICE_CUSTOMER_ADDRESS",
    "FINANCIAL_CLOSING",
    "FINANCIAL_CLOSING_COMMENTS",
    "FINANCIAL_CLOSING_SUMMARY",
    "SUMMARY_FINANCIAL_CLOSING",
    "RnDBifurcation",
    "BANK_ACCOUNT_STATEMENTS",
    "BANK_TRANSFERS",
    "BANK_TRANSFER_DETAILS",
    "VENDOR_SUMMARY",
    "GROUP_ACCOUNT_OVERVIEW",
    "CRM_LAYOUT",
    "CRM_ENTITY_FIELDS",
    "CRM_EXPORT_TEMPLATES",
    "COMPANY_RA_MAILROOM",
    "VENDOR_BILL",
    "PUBLIC_VENDOR_BILL",
    "SINGLE_VENDOR",
    "BANK_ACCOUNT_BALANCES",
    "SALES_TAX_BUSINESS",
    "TAX_CALCULATOR_TAX_CODE",
    "BOOKKEEPING_ENTITY",
    "SALES_TAX_REGISTRATIONS",
    "BROADCAST_TEMPLATE",
    "BOOKKEEPING_TASKS",
    "PHYSICAL_NEXUS_TRACKER",
    "BOOKS_LOCK_DATE",
    "FILINGS",
    "FILINGS_DETAILS",
    "MESSAGE_EMAIL",
    "AI_RESPONSE",
    "OPEN_ITEMS_BY_TRANSACTIONS",
    "TASK_1120_TRACKER",
    "SIDR_DETAILS",
    "SALES_TAX_TASK",
    "TAX_STATS",
    "TAX_HOME_UPCOMING_DEADLINE",
    "CHAT_QNA",
    "GROUP_SPOCS_AVAILABILITY",
    "PAYROLL",
    "AP_DETAILS",
    "FILING_DRAFT_CARD_DETAILS",
    "BROADCAST_LIST",
    "ADVANCE_TAX_TASKS",
    "ADVANCE_TAX_LIABILITY_AND_EXPENSES",
    "ADVANCE_TAX_STATS",
    "ADVANCE_TAX_UPCOMING_DEADLINE",
    "ESTIMATED_TAX_WATERFALL",
    "IRS_PAYMENTS",
    "ESTIMATED_TAX_DEDUCTIONS",
    "ESTIMATED_TAX_MANUAL_INPUT",
    "ESTIMATED_TAX_ENROLLMENT",
    "ESTIMATED_TAX_SETTINGS",
    "ESTIMATED_TAX_S174",
    "ESTIMATED_TAX_ADJUSTMENTS",
    "ESTIMATED_TAX_FINANCIAL_ADJUSTMENTS",
    "ESTIMATED_TAX_BENEFITS",
    "TICKET_TASKS",
    "CLASSIFICATION",
    "TXN_CLASSIFICATION",
    "AGENT_ACTIONS",
    "UNLAUNCHED_TASK_LIST",
    "AI_ONBOARDING",
    "RECURRING_PAYMENT_CONSENT",
    "USER_COUPON_CODE",
    "ADMIN_COUPONS",
    "GROUP_FREE_TASK",
    "RECYCLE_BIN_DOCUMENT_LIST",
    "CUSTOMER_DEFAULT_PRICES",
    "FINANCIAL_SUMMARY",
    "CASH_BALANCE",
    "ENTITY_PROFITABILITY",
    "ANALYSIS",
    "AR_ANALYSIS",
    "AP_ANALYSIS",
    "AR_AGING",
    "AP_AGING",
    "CATEGORISATION_HISTORY",
    "CATEGORISATION_HISTORY_TXNS",
    "CREDIT_NOTES",
    "CUSTOM_REPORTS",
    "MOM_BREAKDOWN",
    "REVENUE_METRICS",
    "MCP_CONNECTION",
    "IRS_DETAILS",
    "IRS_SUBMISSION_STATUS",
    "SMART_REVIEW",
    "ESTIMATED_TAX_WORKSPACES",
    "ESTIMATED_TAX_RECALCULATION",
    "ESTIMATED_TAX_SNAPSHOTS",
    "BUSINESS_REVIEWS",
    "GroupMemories",
    "FORM_1120_REVIEW",
    "GROUP_MISSING_INFO",
    "ALARM_RULES",
    "STATE_INCOME_TAX",
    "STATE_INCOME_TAX_SALES_DATA",
    "STATE_INCOME_TAX_PAYROLL_DATA",
    "STATE_INCOME_TAX_PROPERTY_DATA",
    "STATE_INCOME_TAX_SETTINGS",
    "CHASER_BATCHES",
    "INCOME_STATEMENT_UPLOAD",
    "HUMAN_SUPPORT",
    "ONBOARDING_GROUP_REVIEW",
    "ONBOARDING_STATE",
    "ENTITY_PROFILE_OVERVIEW",
    "ENTITY_OTHER_DOCUMENTS",
    "ENTITY_RECENT_DOCUMENTS",
    "ENTITY_DOCUMENT_TYPES",
    "ENTITY_EXTRACTION_REVIEW",
    "AdminPerks",
    "PerkTags",
    "GroupRoles",
    "MyPermissions",
    "AGENT_SESSION",
    "INCORPORATION_TRACKER",
    "INCORPORATION_TEMPLATES",
    "INCORPORATION_FORM_TREE",
    "INCORPORATION_STAGE_TEMPLATES",
    "EMAIL_SUPPRESSION",
    "TASK_TAGS",
    "COMPANY_GROUP_TAGS",
    "CHURN_DETAIL",
    "HANDOVER_DETAIL",
    "WEBAUTHN_CREDENTIALS",
    "TERMS_OF_SERVICE",
  ],
  baseQuery: baseQuery,
  endpoints: () => ({}),
});
